/**
 * Minimal formatting for admin-authored blog bodies (a small Markdown subset):
 *   ## Heading   ### Subheading   **bold**   *italic*   [text](url)
 *   - bullet   1. numbered   > quote   ---   ![alt](image-url)
 * Parsed into plain data and rendered as React elements, never as HTML, so the
 * body stays escaped (see BlogPage). Old plain-text posts parse into paragraphs.
 */

export type BlogInline =
  | { type: 'text' | 'bold' | 'italic'; text: string }
  | { type: 'link'; text: string; href: string };

export type BlogBlock =
  | { type: 'h2' | 'h3' | 'p' | 'quote'; content: BlogInline[] }
  | { type: 'ul' | 'ol'; items: BlogInline[][] }
  | { type: 'img'; src: string; alt: string }
  | { type: 'hr' };

const HEADING = /^(#{1,3})\s+(.+)$/;
const BULLET = /^[-*]\s+(.+)$/;
const NUMBERED = /^\d+[.)]\s+(.+)$/;
const QUOTE = /^>\s?(.*)$/;
const DIVIDER = /^(-{3,}|\*{3,}|_{3,})$/;
const IMAGE = /^!\[([^\]\n]*)\]\(([^)\s]+)\)$/;
const LINK = /^\[([^\]\n]+)\]\(([^)\s]+)\)$/;
const INLINE = /(\[[^\]\n]+\]\([^)\s]+\)|\*\*[^*\n]+\*\*|\*[^*\s][^*\n]*\*)/;

/** Only web, site-relative, mail and phone links: blocks javascript:, data: and the like. */
export function isSafeUrl(url: string): boolean {
  return /^(https?:\/\/|\/(?!\/)|mailto:|tel:)/i.test(url);
}

/** Links to BuyMe itself (relative, buyme.lk or any subdomain such as staging). */
export function isInternalUrl(href: string): boolean {
  if (href.startsWith('/')) return true;
  try {
    const host = new URL(href).hostname;
    return host === 'buyme.lk' || host.endsWith('.buyme.lk');
  } catch {
    return false;
  }
}

export function parseInline(text: string): BlogInline[] {
  return text
    .split(INLINE)
    .filter(Boolean)
    .map((part): BlogInline => {
      const link = part.match(LINK);
      if (link) {
        // Unsafe targets are shown as their text only, never as a clickable link.
        return isSafeUrl(link[2]) ? { type: 'link', text: link[1], href: link[2] } : { type: 'text', text: link[1] };
      }
      if (part.length > 4 && part.startsWith('**') && part.endsWith('**')) {
        return { type: 'bold', text: part.slice(2, -2) };
      }
      if (part.length > 2 && part.startsWith('*') && part.endsWith('*')) {
        return { type: 'italic', text: part.slice(1, -1) };
      }
      return { type: 'text', text: part };
    });
}

export function parseBlogBody(body: string | null | undefined): BlogBlock[] {
  const blocks: BlogBlock[] = [];
  let paragraph: string[] = [];
  let quote: string[] = [];
  let list: { type: 'ul' | 'ol'; items: BlogInline[][] } | null = null;

  const flush = () => {
    if (paragraph.length) blocks.push({ type: 'p', content: parseInline(paragraph.join('\n')) });
    if (quote.length) blocks.push({ type: 'quote', content: parseInline(quote.join('\n')) });
    if (list) blocks.push(list);
    paragraph = [];
    quote = [];
    list = null;
  };

  for (const raw of (body ?? '').replace(/\r\n?/g, '\n').split('\n')) {
    const line = raw.trim();
    if (!line) {
      flush();
      continue;
    }

    if (DIVIDER.test(line)) {
      flush();
      blocks.push({ type: 'hr' });
      continue;
    }

    const image = line.match(IMAGE);
    if (image && isSafeUrl(image[2])) {
      flush();
      blocks.push({ type: 'img', alt: image[1], src: image[2] });
      continue;
    }

    const heading = line.match(HEADING);
    if (heading) {
      flush();
      // A single # is still an h2: the post title is the page's only h1.
      blocks.push({ type: heading[1].length === 3 ? 'h3' : 'h2', content: parseInline(heading[2]) });
      continue;
    }

    const quoted = line.match(QUOTE);
    if (quoted) {
      if (paragraph.length || list) flush();
      quote.push(quoted[1]);
      continue;
    }

    const bullet = line.match(BULLET);
    const item = bullet ?? line.match(NUMBERED);
    if (item) {
      const type = bullet ? 'ul' : 'ol';
      if (paragraph.length || quote.length || (list && list.type !== type)) flush();
      list ??= { type, items: [] };
      list.items.push(parseInline(item[1]));
      continue;
    }

    if (list || quote.length) flush();
    paragraph.push(line);
  }
  flush();

  return blocks;
}

/** Body with formatting removed, for excerpts and meta descriptions. */
export function blogPlainText(body: string | null | undefined): string {
  return parseBlogBody(body)
    .flatMap((block) => ('items' in block ? block.items : 'content' in block ? [block.content] : []))
    .map((inlines) => inlines.map((i) => i.text).join(''))
    .join(' ');
}
